import { AiImportResponseContent, AiImportResponseTypes, ChunkMessage } from "@/constants/enums";
import type { AiImportStatusResponse } from "@/graphql/__generated__/graphql";

/**
 * Mapping from backend AI import response content to step indices used in the UI
 */
export const CONTENT_TO_STEP_INDEX: Record<string, number> = {
  [AiImportResponseContent.INITIALIZING]: 0,
  [AiImportResponseContent.PARSING]: 1,
  [AiImportResponseContent.CHUNKING]: 2,
  [AiImportResponseContent.INDEXING]: 3,
  [AiImportResponseContent.SUMMARY_GENERATION]: 4,
  [AiImportResponseContent.SUMMARY_AGGREGATION]: 5,
  [AiImportResponseContent.DATA_EXTRACTION]: 6,
  [AiImportResponseContent.WRITING_TO_DATABASE]: 7,
  [AiImportResponseContent.AI_IMPORT_COMPLETED]: 8,
} as const;

/**
 * Localization keys for the 9 AI import steps, matching enum order
 */
export const STEP_KEYS: string[] = [
  "initializing",
  "parsing",
  "chunking",
  "indexing",
  "summarygeneration",
  "summaryaggregation",
  "dataextraction",
  "writingtodatabase",
  "aiimportcompleted",
];

/**
 * Progress bounds per step derived from live SSE - 9 steps total
 */
export const STEP_PROGRESS: Array<{ start: number; end: number }> = [
  { start: 0, end: 10 },
  { start: 11, end: 20 },
  { start: 21, end: 30 },
  { start: 31, end: 40 },
  { start: 41, end: 50 },
  { start: 51, end: 60 },
  { start: 61, end: 70 },
  { start: 71, end: 80 },
  { start: 81, end: 100 },
];

/**
 * Returns true if the provided content marks the final step
 */
export function isFinalContent(content: string | undefined): boolean {
  return content === AiImportResponseContent.AI_IMPORT_COMPLETED;
}

/**
 * Returns the progress percentage for a given step index.
 * If final, returns 100.
 */
export function progressFromStepIndex(stepIndex: number): number {
  if (stepIndex === CONTENT_TO_STEP_INDEX[AiImportResponseContent.AI_IMPORT_COMPLETED]) return 100;
  const bounds = STEP_PROGRESS[stepIndex];
  return bounds?.start ?? 0;
}

/**
 * Parses raw SSE or concatenated JSON chunks into an array of JSON objects.
 * Supports:
 * - Plain JSON object
 * - SSE frames with data: lines (possibly multi-line)
 * - Concatenated JSON objects without delimiters
 */
export function parseKarlChunk(raw: string): Array<any> {
  const items: any[] = [];

  // Fast-path: try direct JSON (single object)
  try {
    const direct = JSON.parse(raw);
    if (direct && typeof direct === "object") {
      items.push(direct);
      return items;
    }
  } catch (_) {
    // not plain JSON, continue
  }

  // If contains SSE framing, extract JSON from data: lines across frames
  if (raw.includes("data:") || raw.includes("event:") || raw.includes("id:")) {
    const frames = raw.split(/\n\n+/);
    for (const frame of frames) {
      if (!frame.trim()) continue;

      const dataLines = frame
        .split(/\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .filter(Boolean);

      if (dataLines.length === 0) continue;

      const dataPayload = dataLines.join("\n");
      try {
        const obj = JSON.parse(dataPayload);
        items.push(obj);
      } catch (_) {
        // ignore non-JSON data payloads
      }
    }
  }

  // Fallback: handle concatenated JSON objects without SSE framing
  if (items.length === 0) {
    const parts = raw
      .replace(/}\s*\{/g, "}|||{")
      .split("|||")
      .map((s) => s.trim())
      .filter(Boolean);
    for (const part of parts) {
      try {
        const obj = JSON.parse(part);
        items.push(obj);
      } catch (_) {
        // ignore invalid fragment
      }
    }
  }

  return items;
}

/**
 * Given an array of runtime steps (with id fields), returns the current step id.
 * Falls back to -1 when steps are empty or id is not available.
 */
export function computeLastIndex(steps: Array<{ id: number }>): number {
  if (!steps || steps.length === 0) return -1;
  const last = steps[steps.length - 1];
  return typeof last?.id === "number" ? last.id : -1;
}

/**
 * Given runtime steps (with id fields), compute current progress percentage.
 */
export function computeProgressFromSteps(steps: Array<{ id: number }>): number {
  if (!steps || steps.length === 0) return 0;
  const last = steps[steps.length - 1];
  if (!last) return 0;
  if (last.id === CONTENT_TO_STEP_INDEX[AiImportResponseContent.AI_IMPORT_COMPLETED]) return 100;
  const { start } = STEP_PROGRESS[last.id] ?? ({ start: 0 } as any);
  return start ?? 0;
}

export interface AiImportDisplayStep {
  id: number;
  node: string;
  type: AiImportResponseTypes.PROGRESS;
  title: string;
  content: string;
}

/**
 * Build UI display steps for the progress list
 */
export function buildAiImportDisplaySteps(
  getLocalizedStepContent: (_stepIndex: number, _messageType: ChunkMessage) => string,
  lastIndex: number,
  progress: number
): AiImportDisplayStep[] {
  const totalSteps = STEP_KEYS.length;
  const steps: AiImportDisplayStep[] = [];
  for (let i = 0; i < totalSteps; i++) {
    const title = getLocalizedStepContent(i, ChunkMessage.TITLE);
    const isCompleted = i < lastIndex || progress === 100;
    const content = isCompleted
      ? getLocalizedStepContent(i, ChunkMessage.END_MESSAGE)
      : getLocalizedStepContent(i, ChunkMessage.START_MESSAGE);
    steps.push({ id: i, node: "", type: AiImportResponseTypes.PROGRESS, title, content });
  }
  return steps;
}

/**
 * Utility to extract publication name for a given import id
 */
export function getPublicationNameForId(
  aiImportStatuses: AiImportStatusResponse[],
  aiImportId: number
): string | undefined {
  const status = aiImportStatuses.find((s) => s.id === aiImportId);
  const name = status?.publicationNames?.[0];
  if (!name) return undefined;
  return name.endsWith(".pdf") ? name : `${name}.pdf`;
}
